import {useEffect, useLayoutEffect, useRef} from 'react'
import {UpdateLocationCallbackType} from 'components/data/WithUrlUpdater'
import {SearchUrlParams} from 'modules/search/types'

import {getUserDeviceDetails} from '@vio/core/src/_web/utils/userAgent/device'

// Pre-paint: close the race where Back fires before the sentinel lands (new tab → about:blank).
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * Routes the browser back button on AP to SRP via the same path as the
 * "Go back to search" button.
 */
export const useInjectSrpHistory = (
  query: SearchUrlParams,
  changeLocation: UpdateLocationCallbackType
) => {
  const hasInjectedSentinel = useRef(false)
  // Refs so the popstate listener can attach once and still read fresh values.
  // AP re-renders give new query/changeLocation refs, which would otherwise
  //  detach/re-attach the listener and drop Back events fired mid-swap.
  const queryRef = useRef(query)
  const changeLocationRef = useRef(changeLocation)
  queryRef.current = query
  changeLocationRef.current = changeLocation

  useIsomorphicLayoutEffect(() => {
    if (hasInjectedSentinel.current) return
    // Skip Safari mobile: it handles Back natively and the sentinel can fight its bfcache.
    // Read UA synchronously — Redux meta populates async and misses the first layoutEffect.
    const {userAgentDetails} = getUserDeviceDetails()
    if (userAgentDetails.isSafari && userAgentDetails.isMobile) return
    if (history.state?.hasBackNavSentinel) return

    // Attach the listener BEFORE mutating history so an early Back press
    //  (hit between sentinel push and effect completion) is still caught.
    const onPopState = (event: PopStateEvent) => {
      if (!event.state?.hasBackNavSentinel) return
      changeLocationRef.current(queryRef.current, {method: 'replace'})
    }
    window.addEventListener('popstate', onPopState)

    const currentState = {...(history.state ?? {})}
    history.replaceState(
      {...currentState, hasBackNavSentinel: true},
      '',
      window.location.href
    )
    history.pushState(currentState, '', window.location.href)
    hasInjectedSentinel.current = true

    return () => window.removeEventListener('popstate', onPopState)
  }, [])
}
